import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Card,
  RadioButton,
  Section,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  AttendanceEmployeesSettingExtraHoursBaselineEnum,
  AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum,
  AttendanceEnumsBalanceCarryoverResetFrequencyEnum,
  AttendanceOvertimePool,
  SettingPageAttendanceBalanceCarryoverFragment,
  SettingPageEmployeesSettingFragment,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import ConfirmUpdateModal from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/shared/ConfirmUpdateModal'
import {
  getBaselineOptions,
  getResetFrequencyOptions,
  getResetFrequencyPeriod,
  getTransferFrequencyOptions,
  isTransferFrequencyCompatibleWithPools,
  validateTransferAndResetCompatibility,
} from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/utils/hoursCalculationUtils'
import { useCreateAttendanceBalanceCarryover } from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useCreateAttendanceBalanceCarryover'
import { useUpdateAttendanceBalanceCarryover } from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateAttendanceBalanceCarryover'
import useUpdateAttendanceSetting from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateAttendanceSetting'
import AttendanceSettingForm from 'modules/attendance/lib/forms/AttendanceSettingForm'
import BalanceCarryoverForm from 'modules/attendance/lib/forms/BalanceCarryoverForm'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

export interface Props {
  attendanceSetting: AttendanceSetting
  balanceCarryover?: SettingPageAttendanceBalanceCarryoverFragment
  overtimePools: AttendanceOvertimePool[]
}

const ExtraHoursCalculation = ({ attendanceSetting, balanceCarryover, overtimePools }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { createBalanceCarryover } = useCreateAttendanceBalanceCarryover()
  const { updateBalanceCarryover } = useUpdateAttendanceBalanceCarryover()

  const createBalanceCarryoverForm = (
    attendanceBalanceCarryover?: SettingPageAttendanceBalanceCarryoverFragment
  ) =>
    new BalanceCarryoverForm({
      frequency_number: attendanceBalanceCarryover?.frequencyNumber || 1,
      reset_frequency: attendanceBalanceCarryover?.resetFrequency || 'monthly',
    })

  const [balanceCarryoverForm, _setBalanceCarryoverForm] = React.useState(
    createBalanceCarryoverForm(balanceCarryover)
  )

  const createSettingForm = (setting: AttendanceSetting) =>
    new AttendanceSettingForm({
      extra_hours_baseline: setting.attributes.extra_hours_baseline,
      extra_hours_transfer_frequency: setting.attributes.extra_hours_transfer_frequency,
    })

  const balanceResetFrequencyField = balanceCarryoverForm.get('reset_frequency')
  const balanceFrequencyNumberField = balanceCarryoverForm.get('frequency_number')

  const [settingForm, setSettingForm] = React.useState(createSettingForm(attendanceSetting))
  const extraHoursBaselineField = settingForm.get('extra_hours_baseline')
  const extraHoursTransferFrequencyField = settingForm.get('extra_hours_transfer_frequency')

  const { loading, updateSetting } = useUpdateAttendanceSetting(settingForm, attendanceSetting)
  const [selected, setSelected] = React.useState(attendanceSetting.extraHoursBaseline)
  const [selectedTransferFrequency, setselectedTransferFrequency] = React.useState(
    attendanceSetting.extraHoursTransferFrequency
  )
  const [selectedResetFrequency, setSelectedResetFrequency] = React.useState(
    balanceCarryover?.resetFrequency || 'monthly'
  )

  const [validationError, setValidationError] = React.useState<string | undefined>()
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const [showUpdateConfirmation, setShowUpdateConfirmation] = React.useState(false)
  const validateTransferAndReset = () => {
    return validateTransferAndResetCompatibility(
      extraHoursTransferFrequencyField.value,
      balanceResetFrequencyField.value
    )
  }

  const saveBalanceCarryover = async (attendanceSettingId: number) => {
    const frequencyValue = balanceCarryoverForm.get('frequency_number').value
    const frequencyNumber = parseInt(frequencyValue) || 1
    const resetFrequency = balanceCarryoverForm.get('reset_frequency').value

    if (balanceCarryover?.id) {
      await updateBalanceCarryover({
        id: balanceCarryover.id,
        attendanceEmployeesSettingId: attendanceSettingId,
        resetFrequency,
        frequencyNumber,
      })
    } else {
      await createBalanceCarryover({
        attendanceEmployeesSettingId: attendanceSettingId,
        resetFrequency,
        frequencyNumber,
      })
    }

    balanceCarryoverForm.cleanAll()
  }

  const onSubmit = () => {
    const validation = validateTransferAndReset()
    if (!validation.isValid) {
      return
    }

    updateSetting()
    if (balanceCarryoverForm.isDirty) {
      saveBalanceCarryover(attendanceSetting.attributes.id)
    }
    onClose()
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const validation = validateTransferAndReset()
    if (!validation.isValid) {
      return
    }

    const settingId = +newSetting.id

    if (balanceCarryoverForm.isDirty) {
      await saveBalanceCarryover(settingId)
    }

    const routeParams = {
      id: 'time_tracking',
      settingId: settingId.toString(),
      tabId: 'work_time_categories',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  useEffect(() => {
    setSettingForm(createSettingForm(attendanceSetting))
    setSelected(attendanceSetting.extraHoursBaseline)
    setselectedTransferFrequency(attendanceSetting.extraHoursTransferFrequency)
    setSelectedResetFrequency(balanceCarryover?.resetFrequency || 'monthly')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceSetting.attributes.id])

  const onChangeBaseline = (option: AttendanceEmployeesSettingExtraHoursBaselineEnum) => {
    extraHoursBaselineField.set(option)
    setSelected(option)
  }

  const onChangeFrequency = (option: AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum) => {
    extraHoursTransferFrequencyField.set(option)
    setselectedTransferFrequency(option)
    const validation = validateTransferAndReset()
    setValidationError(validation.errorMessage)
  }

  const onChangeResetFrequency = (option: AttendanceEnumsBalanceCarryoverResetFrequencyEnum) => {
    balanceResetFrequencyField.set(option)
    setSelectedResetFrequency(option)
    const validation = validateTransferAndReset()
    setValidationError(validation.errorMessage)
  }

  const onClose = () => {
    setOpenEffectiveAtFormModal(false)
  }

  const handleConfirmUpdate = () => {
    setShowUpdateConfirmation(false)
    setOpenEffectiveAtFormModal(true)
  }

  const handleCancelUpdate = () => {
    setShowUpdateConfirmation(false)
  }

  const handleSave = () => {
    const needsConfirmation = !isTransferFrequencyCompatibleWithPools(
      extraHoursTransferFrequencyField.value,
      overtimePools
    )
    if (needsConfirmation) {
      setShowUpdateConfirmation(true)
    } else {
      setOpenEffectiveAtFormModal(true)
    }
  }

  const baselineOptions = getBaselineOptions()
  const transferFrequencyOptions = getTransferFrequencyOptions()
  const resetFrequencyOptions = getResetFrequencyOptions()

  const disableSubmitButton = !!validationError || loading
  const showPeriodForm = selectedResetFrequency !== 'never'

  return (
    <Section
      illustration="performance"
      title={i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_calculation.section.title'
      )}
      description={i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_calculation.section.description'
      )}
    >
      <Box
        flexDirection="column"
        gap="s8"
        background="grey200"
        padding="s24"
        borderRadius={{
          all: 'abs008',
        }}
      >
        <Box gap="s4" paddingBottom="s8">
          <Text size="200" weight="semibold">
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_calculation.baseline.title'
            )}
          </Text>
          <Text size="200">
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_calculation.baseline.description'
            )}
          </Text>
        </Box>
        <Box gap="s8" paddingBottom="s8">
          {baselineOptions.map((option) => (
            <Card key={option.value}>
              <Card.Title
                size="small"
                action={
                  <RadioButton
                    size="medium"
                    name={option.value}
                    onChange={() => onChangeBaseline(option.value)}
                    value={option.value}
                    checked={selected === option.value}
                  />
                }
              >
                {option.label}
              </Card.Title>
              <Card.Description>{option.description}</Card.Description>
            </Card>
          ))}
        </Box>
        <Box gap="s4" paddingBottom="s8">
          <Text size="200" weight="semibold">
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.title'
            )}
          </Text>
          <Text size="200">
            {i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.description'
            )}
          </Text>
        </Box>
        <Select
          field={extraHoursTransferFrequencyField}
          value={selectedTransferFrequency}
          options={transferFrequencyOptions}
          onChange={(field) => onChangeFrequency(field.value)}
        />

        <Box gap="s16" paddingTop="s8">
          <Box gap="s8">
            <Box gap="s4" paddingBottom="s8">
              <Text size="200" weight="semibold">
                {i18n.t(
                  'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.title'
                )}
              </Text>
              <Text size="200">
                {i18n.t(
                  'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.description'
                )}
              </Text>
            </Box>
            <Select
              field={balanceResetFrequencyField}
              value={selectedResetFrequency}
              options={resetFrequencyOptions}
              onChange={(field) => onChangeResetFrequency(field.value)}
            />
          </Box>

          {showPeriodForm && (
            <>
              <Box flexDirection="row" gap="s8" alignItems="center">
                <Box flexGrow gap="s4">
                  <Text.Normal size="200" color="grey1000" weight="medium">
                    {i18n.t(
                      'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.interval'
                    )}
                  </Text.Normal>
                  <>
                    <TextInput
                      field={balanceFrequencyNumberField}
                      mandatory
                      readonly={balanceCarryoverForm.disableFrequencyNumber}
                    />
                  </>
                </Box>
                <Box flexGrow gap="s4">
                  <Text.Normal size="200" color="grey1000" weight="medium">
                    {i18n.t(
                      'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.period'
                    )}
                  </Text.Normal>
                  <TextField
                    readonly
                    value={getResetFrequencyPeriod(
                      balanceCarryoverForm.get('reset_frequency').value
                    )}
                  />
                </Box>
              </Box>
              <Box flexDirection="row" gap="s4">
                <Text size="200" weight="bold">
                  {i18n.t(
                    'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.note'
                  )}
                </Text>
                <Text size="200">
                  {i18n.t(
                    'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.foot_note'
                  )}
                </Text>
              </Box>
            </>
          )}
        </Box>
        {validationError && (
          <Box padding="s8">
            <Callout type="warning" title="" description={validationError} />
          </Box>
        )}
        {(settingForm.isDirty || balanceCarryoverForm.isDirty) && (
          <Box alignItems="flexEnd" paddingTop="s16">
            <Button type="primary" disabled={disableSubmitButton} onClick={handleSave}>
              {i18n.t('common.save')}
            </Button>
          </Box>
        )}
        {showUpdateConfirmation && (
          <ConfirmUpdateModal onClose={handleCancelUpdate} onConfirm={handleConfirmUpdate} />
        )}
        {openEffectiveAtFormModal && (
          <ManageSettingVersionModal
            onCreate={onCreateSetting}
            onUpdate={onSubmit}
            onClose={onClose}
            running={loading}
            attendanceSettingId={attendanceSetting.attributes.id}
            attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
          />
        )}
      </Box>
    </Section>
  )
}

export default ExtraHoursCalculation
